import React, { useState, useContext } from 'react';
import { Item } from '@react-stately/collections';

import { CatalogContext } from 'src/pages/CatalogPage/CatalogPage';
import { Button, IconSearch, IconMore, Picker, Switch, TextField } from '@riac/design-system';
import { ICatalogSearch } from 'src/pages/CatalogPage/CatalogPage.i';

interface ISearchBar {
  complete: boolean;
  onlyLotsBidOn: boolean;
  lotNumber: string;
  keywords: string;
  perPage: number;
  sortBy: string;
  searchBusy: boolean;
  updateSearch: ICatalogSearch;
  pageNumber: number;
  totalResults: number;
  auctionTypeId: number;
}

export const CatalogHeadSearchBar = (props: ISearchBar) => {
  const [searchInput, setSearchInput] = useState(props.keywords);
  const [lotInput, setLotInput] = useState(props.lotNumber);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const { auctionLive, compactMode, setCompactMode } = useContext(CatalogContext);
  function toggleCompletedLots(e) {
    props.updateSearch({ pageNumber: 1, complete: e });
  }

  function toggleLotsBidOn(e) {
    props.updateSearch({ pageNumber: 1, onlyLotsBidOn: e });
  }

  function keywordChange(e) {
    setSearchInput(e);
  }

  function gotoLotChange(e) {
    setLotInput(e.replace(/[^0-9]/g, ''));
  }

  function doSearch() {
    setLotInput('');
    props.updateSearch({
      pageNumber: 1,
      keywords: encodeURIComponent(searchInput),
      lotNumber: '',
    });
  }

  function doGotoLot() {
    setSearchInput('');
    props.updateSearch({ lotNumber: lotInput });
  }
  const totalPages = Math.ceil(props.totalResults / props.perPage);
  
  const perPageOptions = [
    { value: '50', label: '50 Items' },
    { value: '100', label: '100 Items' },
    { value: '150', label: '150 Items' },
    { value: '200', label: '200 Items' },
  ];

  function perPageChange(e): void {
    if(parseInt(e.value) !== props.perPage) {
      props.updateSearch({
        pageNumber: 1,
        perPage: parseInt(e.value),
      });
    }
  }

  function sortChange(e): void {
    if(e.value !== props.sortBy) {
      props.updateSearch({
        pageNumber: 1, sortBy: e.value,
      });
    }
  }

  function pageSelect(e): void {
    if(parseInt(e.value) !== props.pageNumber) {
      props.updateSearch({
        pageNumber: parseInt(e.value),
      });
    }
  }
  const pagesDropdown = [];
  for(let i = 1; i <= totalPages; i++) {
    pagesDropdown.push(i);
  }
  let sortByOptions = [
    { value: 'LotNumber,ASC', label: 'Lot # (ASC)' },
    { value: 'LotNumber,DESC', label: 'Lot # (DESC)' },
    { value: 'CurrentBid,ASC', label: 'Current Bid (ASC)' },
    { value: 'CurrentBid,DESC', label: 'Current Bid (DESC)' },
    { value: 'LowEst,ASC', label: 'Est. Low Price (ASC)' },
    { value: 'LowEst,DESC', label: 'Est. Low Price (DESC)' },
    { value: 'HighEst,ASC', label: 'Est. High Price (ASC)' },
    { value: 'HighEst,DESC', label: 'Est. High Price (DESC)' },
    { value: 'ManufacturerName,ASC', label: 'Manufacturer (ASC)' },
    { value: 'ManufacturerName,DESC', label: 'Manufacturer (DESC)' },
    { value: 'ModelDescription,ASC', label: 'Model (ASC)' },
    { value: 'ModelDescription,DESC', label: 'Model (DESC)' },
    { value: 'ItemInterest,ASC', label: 'Activity (ASC)' },
    { value: 'ItemInterest,DESC', label: 'Activity (DESC)' },
  ];

  if(props.auctionTypeId === TIMED_AUCTION_TYPE_ID) {
    sortByOptions = sortByOptions.filter((x) => !x.label.includes('Est. '));
  } else {
    sortByOptions = sortByOptions.filter((x) => !x.label.includes('Bid '));
    sortByOptions = sortByOptions.filter((x) => !x.label.includes('Activity '));
  }

  return (
    <div className='flex flex-col flex-1 w-full justify-stretch gap-3'>
      <div className='flex md:flex-row flex-col flex-1 justify-stretch gap-5'>
        <div className='flex flex-row gap-5 flex-1'>
          <TextField
            type='text'
            value={searchInput}
            onChange={keywordChange}
            className='flex-1'
            inputClassName='h-9'
            onKeyDown={(evt) => {
              if(evt.key === 'Enter')
              {
                doSearch();
              }
            }}
            aria-label='Search keyword'
            placeholder='Search keyword'
            after={
              <Button size='sm' onPress={doSearch} className='min-w-9 h-9 p-0 flex items-center justify-center'>
                <IconSearch className='w-5 h-5'/>
              </Button>
            }
          />
        </div>
        <div className='flex flex-row gap-5 flex-0'>
          <TextField
            type='text'
            value={lotInput}
            onChange={gotoLotChange}
            className='flex-1'
            inputClassName='h-9'
            onKeyDown={(evt) => {
              if(evt.key === 'Enter')
              {
                doGotoLot();
              }
            }}
            aria-label='Go to lot number'
            placeholder='Go to lot number'
            after={
              <Button size='sm' onPress={doGotoLot} className='min-w-9 h-9 p-0 flex items-center justify-center'>
                <IconSearch className='w-5 h-5'/>
              </Button>
            }
          />
          <Button variant='secondary' className='place-self-end flex w-9 h-9 p-0 items-center justify-center' onPress={() => {
            setAccordionOpen((x) => !x);
          }}>
            <IconMore/>
          </Button>
        </div>
      </div>
      {accordionOpen &&
        <div className='flex flex-1 flex-wrap flex-col lg:flex-row gap-3 items-stretch justify-stretch w-full'>
          <div className='flex-1 bg-surface-dark p-3 sm:min-w-96 flex flex-col gap-1 justify-center'>
            {auctionLive && (
              <Switch
                label='Show completed lots'
                onChange={() => toggleCompletedLots(!props.complete)}
                checked={props.complete}
              />
            )}
            <Switch
              label='Show in compact mode'
              checked={compactMode === 'true'}
              onChange={() => setCompactMode(compactMode === 'true' ? 'false' : 'true')}
              className='hidden lg:flex'
            />
            <Switch
              label='Show only lots I bid on'
              checked={props.onlyLotsBidOn}
              onChange={() => toggleLotsBidOn(!props.onlyLotsBidOn)}
            />
          </div>
          <div className='flex-1 flex-grow-[100] bg-surface-dark p-3 flex flex-col gap-3 lg:min-w-96'>
            <Picker
              label = 'Sort By'
              selectedKey = {sortByOptions.find((opt) => opt.value === props.sortBy)?.value}
              onSelectionChange = {(value) => sortChange({ value })}
            >
              {sortByOptions.map((sortBy) => (
                <Item key = {sortBy.value}>{sortBy.label}</Item>
              ))}
            </Picker>
            <div className='flex flex-row gap-3'>
              <Picker
                label='Page'
                selectedKey = {props.pageNumber.toString()}
                onSelectionChange = {(page) => pageSelect({ value: page })}
                isDisabled = {props.searchBusy || totalPages === 0}
              >
                {pagesDropdown.map((page) => (
                  <Item key = {page}>
                    {page.toString()}
                  </Item>
                ))}
              </Picker>
              <Picker
                label = 'Per Page'
                selectedKey = {perPageOptions.find((opt) => parseInt(opt.value) === props.perPage)?.value}
                onSelectionChange = {(value) => perPageChange({ value })}
              >
                {perPageOptions.map((perPage) => (
                  <Item key = {perPage.value}>{perPage.label}</Item>
                ))}
              </Picker>
            </div>
          </div>
        </div>
      }
    </div>
  );
};
